import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@fuxi/eevee-icon';
import { Button } from 'antd-mobile';
import { useLocation } from 'react-router';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useGetState } from 'ahooks';
import { useWebRtc } from '@/hooks/useWebRtc';
import { Toast } from '@/utils';
import { Header } from '@/components';
import useNav from '@/hooks/useNav';
import { ALL_PAGES, MachineState, storagePrefixV2 } from '@/constant';
import service from '@/service';
import { setIsCanClickTabBar, setIsCanNavToMassageControl } from '@/store/device';
import { useSocketIO } from '@/hooks';
import cx from './index.module.scss';

interface Props {}

const positionAidImg = storagePrefixV2 + '/positioning-aid-frame.png';

const PosAssist: React.FC<Props> = () => {
  const nav = useNav();
  const dispatch = useDispatch();
  const location = useLocation();
  const [isMachineStarting, setIsMachineStarting, getIsMachineStarting] = useGetState(true);
  const [isBtnLoading, setIsBtnLoading] = useState(false);
  const [isLock, setIsLock] = useState(false);
  const [deviceList, setDeviceList] = useState<DeviceItem[]>([]);
  const [isPlayVideo, setIsPlayVideo] = useState(false);
  const [isCompleting, setIsCompleting, getIsCompleting] = useGetState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isConnectedRTCApi = useRef(false);
  const [isConnectSocket, setIsConnectSocket] = useState(false);
  const isUnmount = useRef(false);

  const deviceInfo: DeviceItem = useMemo(() => location?.state || {}, [location?.state]);

  const canBack = useMemo(() => !(isMachineStarting || isCompleting), [isCompleting, isMachineStarting]);

  const { socketInstance, initSocket, destroySocket } = useSocketIO({ autoCloseOnUnmount: false });

  const { start, stop } = useWebRtc({
    socketInstance,
    videoRef,
    rtcAPIType: 'auxiliary-position',
    deviceId: deviceInfo?.deviceId,
    onRTCConnectionStateChange(state) {
      if (state === 'failed') {
        !isUnmount.current && Toast.fail('流媒体连接失败，请稍后重试', { afterClose: () => nav(-1) });
      }
      if (state === 'connected') {
        setIsMachineStarting(false);
      }
    },
  });

  const curDeviceInfo = useMemo(
    () => deviceList.find(item => item.deviceId === deviceInfo?.deviceId),
    [deviceInfo?.deviceId, deviceList]
  );

  /** 建立socket连接 */
  const establishSocketConnection = useCallback(async () => {
    const { data } = await service.device.getConnectionInfo(deviceInfo?.deviceId);
    initSocket(data);

    socketInstance.current.onConnect(() => {
      console.log('与服务器连接');
      setIsConnectSocket(true);
    });
  }, [deviceInfo?.deviceId, initSocket, socketInstance]);

  // 监听到视频播放事件
  const onVideohasPlay = useCallback(() => {
    setIsPlayVideo(true);
  }, []);

  const getDeviceList = useCallback(async () => {
    const { data } = await service.device.getDeviceList();
    setDeviceList(data);
  }, []);

  // 停驻解锁
  const toggleLockState = useCallback(
    (state: 'lock' | 'unlock') => () => {
      if (!(!isBtnLoading && deviceInfo?.deviceId)) return;
      return new Promise(async (resolve, reject) => {
        setIsLock(state === 'lock');
        let timer = null;
        setIsBtnLoading(true);

        const handleOk = () => {
          state !== 'lock' && Toast.success('解锁成功');
          setIsBtnLoading(false);
          resolve('');
        };

        try {
          await socketInstance.current[state === 'lock' ? 'parking' : 'cancelParking']({ timeout: 40000 });
          handleOk();
        } catch {
          setIsBtnLoading(false);
          clearTimeout(timer);
          reject('');
        }
      });
    },
    [deviceInfo?.deviceId, isBtnLoading, socketInstance]
  );

  const handleCompletePositon = useCallback(async () => {
    setIsCompleting(true);
    try {
      await stop();
      isConnectedRTCApi.current = false;

      await toggleLockState('lock')();
      Toast.success('机器驻停已完成');
    } catch {
      Toast.show('操作失败，请重试');
    } finally {
      nav(ALL_PAGES.DEVICE_MANAGEMENT);
    }
  }, [nav, setIsCompleting, stop, toggleLockState]);

  useEffect(() => {
    establishSocketConnection();
    getDeviceList();
    dispatch(setIsCanClickTabBar(false));
    const persistVideo = videoRef.current;
    persistVideo?.addEventListener('play', onVideohasPlay);

    return () => {
      dispatch(setIsCanClickTabBar(true));
      persistVideo?.removeEventListener('play', onVideohasPlay);
      isUnmount.current = true;
      dispatch(setIsCanNavToMassageControl(false));
      stop(() => {
        console.log('关闭连接');
        destroySocket();
        setTimeout(() => dispatch(setIsCanNavToMassageControl(true)), 1500);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!(isMachineStarting && isConnectSocket)) return;

    start(
      () => (isConnectedRTCApi.current = true),
      () => nav('/device/management')
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMachineStarting, isConnectSocket]);

  return (
    <div className={cx('pos-assist')}>
      <Header
        title="定位辅助"
        showBack={canBack}
        backConfig={{ text: '设备管理', action: () => nav(ALL_PAGES.DEVICE_MANAGEMENT) }}
      />
      {isCompleting ? (
        <div className={cx('completing')}>
          <Loading3QuartersOutlined spin className={cx('loading-icon')} />
          {/* <Loading3QuartersOutlined spin className={cx('loading-spinner')} /> */}
          <div className={cx('loading-text')}>机器定位完成，机器停驻中</div>
        </div>
      ) : (
        <>
          <div className={cx('loading-content', { show: isMachineStarting })}>
            <div className={cx('loading')}>
              {/* <FrameAnimation url={<LoadingOutlined />} count={59} aspectRatio={1} width={200} duration={2} /> */}
              <Loading3QuartersOutlined spin className={cx('loading-icon')} />
              <div className={cx('text')}>机器定位中，请勿退出当前页面</div>
            </div>
          </div>
          <div className={cx('pos-assist-box', { show: !isMachineStarting })}>
            <div className={cx('left')}>
              <div className={cx('left-wrapper')}>
                <video ref={videoRef} autoPlay muted className={cx('play-content', { show: isPlayVideo })}></video>
                <div className={cx('horizontal-line', 'line', { show: isPlayVideo })} />
                <div className={cx('line-text', { show: isPlayVideo })}>水平辅助线</div>
                <div className={cx('vertical-line', 'line', { show: isPlayVideo })} />
                <div className={cx('line-text', 'vertical-text-position', { show: isPlayVideo })}>垂直辅助线</div>
                <img
                  src={positionAidImg}
                  alt="pos-refer"
                  className={cx('positioning-aid-frame-img', { show: isPlayVideo })}
                />
              </div>
            </div>
            <div className={cx('right')}>
              <div className={cx('header')}>
                <div className={cx('machine-info')}>
                  <div className={cx('machine-text')}>{curDeviceInfo?.deviceName}</div>
                  <div className={cx('machine-state', curDeviceInfo?.state?.toLowerCase())}>
                    {curDeviceInfo?.state === 'Busy' && <Icon name="servicing-icon" className={cx('servicing-icon')} />}

                    {MachineState[curDeviceInfo?.state]}
                  </div>
                </div>
                <div className={cx('operate', { 'is-loading': isBtnLoading })} onClick={toggleLockState('unlock')}>
                  <div className={cx('icon-wrapper')}>
                    {isBtnLoading ? <Loading3QuartersOutlined spin /> : <Icon name="unlock" />}
                  </div>
                  <div className={cx('operate-text')}>解锁</div>
                </div>
              </div>
              <div className={cx('right-content')}>
                <div className={cx('tips')}>
                  <div className={cx('tips-text')}>
                    <div className={cx('tips-text-title')}>温馨提示</div>
                    <div className={cx('tips-text-box')}>
                      <span>1</span>
                      <span>请将按摩床边缘平行于两侧辅助线</span>
                    </div>
                    <div className={cx('tips-text-box')}>
                      <span>2</span>
                      <span>请将按摩床脸洞处置于白色圆框中</span>
                    </div>
                  </div>
                </div>
                <Button
                  className={cx('button')}
                  loading={isBtnLoading}
                  loadingText={isLock ? '机器停驻中' : '机器解锁中'}
                  onClick={handleCompletePositon}
                >
                  定位完成并停驻机器
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PosAssist;
